import type { OpportunitiesFilters } from "../components/table/OpportunitiesTableToolbar/OpportunitiesTableToolbar";
import type { OpportunityTabKey } from "../types/opportunities.types";

export const OPPORTUNITY_FILTER_ALL = "all" as const;

/** Primary dataset — preselected on first load and on new-set creation. */
export const DEFAULT_OPPORTUNITY_DATASET = "Topics" as const;

/** Baseline used by "Clear filters" and by `hasActiveOpportunityFilters`. Every key is `all`
 *  so the preselected Topics dataset still narrows exports and empty-state copy. */
export const DEFAULT_OPPORTUNITY_FILTERS: OpportunitiesFilters = {
  action: OPPORTUNITY_FILTER_ALL,
  dataset: OPPORTUNITY_FILTER_ALL,
  feature: OPPORTUNITY_FILTER_ALL,
  subFeature: OPPORTUNITY_FILTER_ALL,
  engine: OPPORTUNITY_FILTER_ALL,
};

/** Filters preselected on first navigation to the page and on set-switch — points the user at
 *  the primary Topics workflow without locking them in (Clear filters → DEFAULT_OPPORTUNITY_FILTERS). */
export const INITIAL_OPPORTUNITY_FILTERS: OpportunitiesFilters = {
  ...DEFAULT_OPPORTUNITY_FILTERS,
  dataset: DEFAULT_OPPORTUNITY_DATASET,
};

export type OpportunitiesLinkSearchInput = Partial<{
  setId: string;
  q: string;
  scriptId: string;
  orderId: string;
  opportunityId: string;
  createSet: boolean;
}> & {
  tab: OpportunityTabKey;
};

/** Map table filter state to `/opportunities` URL search (includes legacy `flowName`). */
export function buildOpportunitiesLinkSearch(
  filters: OpportunitiesFilters,
  overrides: OpportunitiesLinkSearchInput,
) {
  return {
    q: "",
    action: filters.action,
    dataset: filters.dataset,
    flowName: filters.dataset,
    feature: filters.feature,
    subFeature: filters.subFeature,
    engine: filters.engine,
    ...overrides,
  };
}

export function buildInitialOpportunitiesLinkSearch(
  overrides: OpportunitiesLinkSearchInput,
) {
  return buildOpportunitiesLinkSearch(INITIAL_OPPORTUNITY_FILTERS, overrides);
}

export function buildDefaultOpportunitiesLinkSearch(
  overrides: OpportunitiesLinkSearchInput,
) {
  return buildOpportunitiesLinkSearch(DEFAULT_OPPORTUNITY_FILTERS, overrides);
}

export function isDefaultOpportunityFilter(
  key: keyof OpportunitiesFilters,
  value: string,
): boolean {
  return value === DEFAULT_OPPORTUNITY_FILTERS[key];
}

export function hasActiveOpportunityFilters(
  filters: OpportunitiesFilters,
  search = "",
): boolean {
  if (search.trim() !== "") return true;
  return (Object.keys(filters) as (keyof OpportunitiesFilters)[]).some(
    (key) => !isDefaultOpportunityFilter(key, filters[key]),
  );
}

/** Badge count — the preselected dataset is not a user choice, so it is not counted. */
export function countActiveOpportunityFilters(filters: OpportunitiesFilters): number {
  return (Object.keys(filters) as (keyof OpportunitiesFilters)[]).filter(
    (key) =>
      !isDefaultOpportunityFilter(key, filters[key]) &&
      filters[key] !== INITIAL_OPPORTUNITY_FILTERS[key],
  ).length;
}

/** Falls back to all datasets when the selected one has no rows in the current set/tab — e.g. the
 *  preselected Topics on a set without Topics, or a stale Angles link after Angles left the pipeline. */
export function resolveAvailableDataset(
  dataset: string,
  availableDatasets: readonly string[],
): string {
  if (dataset === OPPORTUNITY_FILTER_ALL) return dataset;
  return availableDatasets.includes(dataset) ? dataset : OPPORTUNITY_FILTER_ALL;
}
